import React, { useState } from "react";
import axios from "axios";

function AddUserCurr({ type }) {
  const [userID, setUserID] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/create-user`,
        { userID, password, type },
        { withCredentials: true }
      );
      setMessage(response.data.message);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An error occurred.";
      setMessage(errorMessage);
    } finally {
      setUserID("");
      setPassword("");
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleFileSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setMessage("Please upload a CSV file.");
      return;
    }

    setMessage("");
    setLoading(true);

    const reader = new FileReader();
    reader.onload = async (event) => {
      const csvText = event.target.result;
      const lines = csvText.trim().split("\n");
      const headers = lines[0].split(",");

      if (!headers.includes("userID") || !headers.includes("password")) {
        setMessage("CSV must contain 'userID' and 'password' columns.");
        setLoading(false);
        return;
      }

      const users = lines.slice(1).map((line) => {
        const values = line.split(",");
        const user = {};
        headers.forEach((header, index) => {
          user[header.trim()] = values[index]?.trim() ?? "";
        });
        return user;
      });

      try {
        await Promise.all(
          users.map((user) =>
            axios.post(
              `${process.env.REACT_APP_BASE_URL}/admin/create-user`,
              { ...user, type },
              { withCredentials: true }
            )
          )
        );
        setMessage("All users created successfully.");
      } catch (error) {
        setMessage("Some users may not have been created. Check the server.");
      } finally {
        setLoading(false);
        setFile(null);
      }
    };

    reader.readAsText(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (["dragenter", "dragover"].includes(e.type)) {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  return (
    <div className="max-w-md mx-auto p-6 border border-gray-300 rounded-lg shadow-md bg-white">
      <h1 className="text-2xl font-bold text-center text-gray-800 mb-4">
        Add {type}
      </h1>

      {/* Manual Form */}
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="userID" className="block mb-1 text-gray-700 font-semibold">
            {type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()} ID:
          </label>
          <input
            type="text"
            id="userID"
            value={userID}
            onChange={(e) => setUserID(e.target.value)}
            required
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="password" className="block mb-1 text-gray-700 font-semibold">
            Password:
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          type="submit"
          className="w-full py-2 mb-6 bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-600 transition duration-200"
        >
          Create User
        </button>
      </form>

      {/* CSV Upload Form */}
      <form
        onSubmit={handleFileSubmit}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        className="mt-6"
      >
        <div className="mb-4">
          <label className="block mb-2 text-gray-700 font-semibold">
            Upload Users via CSV:
          </label>

          <label
            htmlFor="csvUpload"
            className={`flex items-center justify-center w-full px-4 py-6 border-2 border-dashed rounded-md cursor-pointer transition ${
              dragActive
                ? "border-blue-400 bg-blue-50"
                : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-blue-500 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0l-4 4m4-4l4 4M17 8v12m0 0l-4-4m4 4l4-4" />
            </svg>
            {file ? (
              <span className="text-sm text-gray-700">{file.name}</span>
            ) : (
              <span className="text-sm text-gray-500">
                Click to select or drag a CSV file here
              </span>
            )}
            <input
              id="csvUpload"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />
          </label>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`w-full py-2 mt-2 font-semibold rounded-md transition duration-200 ${
            loading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-green-500 hover:bg-green-600 text-white"
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin h-5 w-5 mr-2 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                ></path>
              </svg>
              Uploading...
            </span>
          ) : (
            "Upload Users CSV"
          )}
        </button>
      </form>

      {/* Message */}
      {message && (
        <div
          className={`mt-4 p-3 rounded-md text-center font-medium ${
            message.includes("success") || message.includes("created")
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {message}
        </div>
      )}
    </div>
  );
}

export default AddUserCurr;
